import { useAccounts } from '../../hooks/useAccounts';
import { useJournalEntry } from '../../hooks/useJournalEntries';
import { ReclassifyModal } from '../journal/ReclassifyModal';

/**
 * Open the reclassify dialog for one worksheet row.
 *
 * The dialog needs the entry's full lines and the chart of accounts, neither of
 * which the worksheet carries. Loading them here — mounted only once a row is
 * actually clicked — keeps a hundred-row worksheet from fetching a hundred
 * entries it will never show.
 *
 * `frozenAccountId` is the account being reconciled: the bank line is the money
 * movement the statement proves, so it stays fixed while the other side is
 * re-accounted or split.
 */
export function ReclassifyLauncher({
  companyId,
  entryId,
  frozenAccountId,
  onClose,
}: {
  companyId: string;
  entryId: string;
  frozenAccountId: string | null;
  onClose: () => void;
}) {
  const { data: entry, isLoading, error } = useJournalEntry(companyId, entryId);
  const { data: accounts = [] } = useAccounts(companyId);

  if (isLoading || (!entry && !error)) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <p className="muted" style={{ margin: 0 }}>Loading entry…</p>
        </div>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <p className="field-error" style={{ marginTop: 0 }}>Could not load that entry.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ReclassifyModal
      entry={entry}
      accounts={accounts}
      frozenAccountId={frozenAccountId}
      onClose={onClose}
    />
  );
}
